/**
 * Admin app entry — `<BrowserRouter>` + AuthProvider + protected
 * app-shell + LoginPage.
 *
 * Served at the root of admin.orboto.io (OCP-124). No URL prefix —
 * the subdomain IS the namespace.
 *
 * Route table:
 *   /login           → LoginPage (public)
 *   /forgot-password → ForgotPasswordPage (public)
 *   /                → HomePage (protected)
 *   /tenants
 *   /codes
 *   /waitlist
 *   /audit           → placeholder NotFoundPage until each
 *                      corresponding feature ticket replaces it
 *   /*               → NotFoundPage (protected)
 */
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { AppShell } from './components/AppShell';
import { AppVersionBadge } from './components/AppVersionBadge';
import { LegacyPrefixRedirect } from './components/LegacyPrefixRedirect';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './lib/auth-context';
import { AdminAccountDetailPage } from './routes/AdminAccountDetailPage';
import { AdminAccountsPage } from './routes/AdminAccountsPage';
import { AdminAffiliatePayoutsPage } from './routes/AdminAffiliatePayoutsPage';
import { AdminAuditPage } from './routes/AdminAuditPage';
import { AdminBillingProvidersPage } from './routes/AdminBillingProvidersPage';
import { AdminTermsPage } from './routes/AdminTermsPage';
import { AdminNotificationsPage } from './routes/AdminNotificationsPage';
import { AdminAiQualityPage } from './routes/AdminAiQualityPage';
import { AdminFinancePage } from './routes/AdminFinancePage';
import { AdminCodesPage } from './routes/AdminCodesPage';
import { AdminDripTemplatesPage } from './routes/AdminDripTemplatesPage';
import { AdminEncryptionPage } from './routes/AdminEncryptionPage';
import { AdminHardDeleteQueuePage } from './routes/AdminHardDeleteQueuePage';
import { AdminPaymentDefaultsPage } from './routes/AdminPaymentDefaultsPage';
import { AdminCodeGrantsPage } from './routes/AdminCodeGrantsPage';
import { AdminDiscountCodesPage } from './routes/AdminDiscountCodesPage';
import { AdminCompanyPackagesPage } from './routes/AdminCompanyPackagesPage';
import { AdminObservabilityPage } from './routes/AdminObservabilityPage';
import { OAuthConsentPage } from './routes/OAuthConsentPage';
import { AdminOAuthClientsPage } from './routes/AdminOAuthClientsPage';
import { AdminPortalCodesPage } from './routes/AdminPortalCodesPage';
import { AdminProductsPage } from './routes/AdminProductsPage';
import { AdminRedeemAttemptsPage } from './routes/AdminRedeemAttemptsPage';
import { AdminFeedbackPage } from './routes/AdminFeedbackPage';
import { AdminSupportInboxPage } from './routes/AdminSupportInboxPage';
import { AdminTenantErrorsPage } from './routes/AdminTenantErrorsPage';
import { AdminReferralsPage } from './routes/AdminReferralsPage';
import { AdminJobDetailPage, AdminJobsPage } from './routes/AdminJobsPage';
import { AdminLicensesPage } from './routes/AdminLicensesPage';
import { AdminLicenseDetailPage } from './routes/AdminLicenseDetailPage';
import { AdminMailOperatorSendsPage } from './routes/AdminMailOperatorSendsPage';
import { AdminMailAnnouncementsPage } from './routes/AdminMailAnnouncementsPage';
import { AdminMailOperatorSuppressionsPage } from './routes/AdminMailOperatorSuppressionsPage';
import { AdminMaintenanceDetailPage, AdminMaintenancePage } from './routes/AdminMaintenancePage';
import { AdminGatewayModelsPage } from './routes/AdminGatewayModelsPage';
import { AdminTrainingDataPage } from './routes/AdminTrainingDataPage';
import { AdminServerDetailPage } from './routes/AdminServerDetailPage';
import { AdminServersPage } from './routes/AdminServersPage';
import { ServerHealthPage } from './routes/ServerHealthPage';
import { AdminSelfHostNotifyPage } from './routes/AdminSelfHostNotifyPage';
import { AdminProvisioningApprovalsPage } from './routes/AdminProvisioningApprovalsPage';
import { AdminSettingsPage } from './routes/AdminSettingsPage';
import { AdminTeamPage } from './routes/AdminTeamPage';
import { AdminApprovalDetailPage, AdminApprovalsPage } from './routes/AdminApprovalsPage';
import { AdminTenantDetailPage } from './routes/AdminTenantDetailPage';
import { AdminTenantHealthPage } from './routes/AdminTenantHealthPage';
import { AdminTenantsPage } from './routes/AdminTenantsPage';
import { AdminWaitlistPage } from './routes/AdminWaitlistPage';
import { ForgotPasswordPage } from './routes/ForgotPasswordPage';
import { OmsCustomerDetailPage } from './routes/oms/CustomerDetailPage';
import { OmsCustomersPage } from './routes/oms/CustomersPage';
import { OmsQuotasPage } from './routes/oms/QuotasPage';
import { OmsSendsPage } from './routes/oms/SendsPage';
import { OmsSuppressionPage } from './routes/oms/SuppressionPage';
import { OmsSystemStatusPage } from './routes/oms/SystemStatusPage';
import { OmsTemplatesPage } from './routes/oms/TemplatesPage';
import { HomePage } from './routes/HomePage';
import { LoginPage } from './routes/LoginPage';
import { NotFoundPage } from './routes/NotFoundPage';
import { SecurityPage } from './routes/SecurityPage';
import { AdminBackupsPage } from './routes/AdminBackupsPage';
import { AdminFileBackupsPage } from './routes/AdminFileBackupsPage';
import { AdminAccessReviewPage } from './routes/AdminAccessReviewPage';

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
      <AuthProvider>
        <AppVersionBadge />
        <Routes>
          {/* Pre-OCP-124 the admin SPA lived at /admin/*. Catch any
              stale bookmarks, browser autofills, or post-login `from`
              state still pointing at the old prefix and rewrite to the
              prefix-less equivalent BEFORE ProtectedRoute captures it. */}
          <Route path="/admin" element={<LegacyPrefixRedirect prefix="/admin" />} />
          <Route path="/admin/*" element={<LegacyPrefixRedirect prefix="/admin" />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          {/* OCP-236: OAuth consent screen — public route; the page
              redirects to /login itself when the operator isn't signed in. */}
          <Route path="/oauth/authorize" element={<OAuthConsentPage />} />
          <Route
            element={
              <ProtectedRoute>
                <AppShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<HomePage />} />
            {/* Feature pages from OCP-1 / 21 / 22 / 23 plug in here. */}
            <Route path="tenants" element={<AdminTenantsPage />} />
            <Route path="tenants/health" element={<AdminTenantHealthPage />} />
            <Route path="tenants/:id" element={<AdminTenantDetailPage />} />
            <Route path="servers" element={<AdminServersPage />} />
            <Route path="server-health" element={<ServerHealthPage />} />
            <Route path="oauth-clients" element={<AdminOAuthClientsPage />} />
            <Route path="servers/:id" element={<AdminServerDetailPage />} />
            <Route path="jobs" element={<AdminJobsPage />} />
            <Route path="jobs/:id" element={<AdminJobDetailPage />} />
            {/* OCP-485 - scheduled maintenance windows (Phase E of OCP-60). */}
            <Route path="maintenance" element={<AdminMaintenancePage />} />
            <Route path="maintenance/:id" element={<AdminMaintenanceDetailPage />} />
            <Route path="gateway-models" element={<AdminGatewayModelsPage />} />
            {/* OCP-615 - training-data intake: consent, volume, purge, exports. */}
            <Route path="training-data" element={<AdminTrainingDataPage />} />
            <Route path="codes" element={<AdminCodesPage />} />
            <Route path="redeem-attempts" element={<AdminRedeemAttemptsPage />} />
            <Route path="feedback" element={<AdminFeedbackPage />} />
            {/* OCP-617 - the team mailbox as the SUP ticket queue. */}
            <Route path="support-inbox" element={<AdminSupportInboxPage />} />
            {/* OCP-557 - escalated tenant errors (cross-tenant inbox). */}
            <Route path="errors" element={<AdminTenantErrorsPage />} />
            <Route path="drip/templates" element={<AdminDripTemplatesPage />} />
            <Route path="accounts" element={<AdminAccountsPage />} />
            <Route path="accounts/:id" element={<AdminAccountDetailPage />} />
            <Route path="products" element={<AdminProductsPage />} />
            <Route path="referrals" element={<AdminReferralsPage />} />
            <Route path="affiliate-payouts" element={<AdminAffiliatePayoutsPage />} />
            <Route path="billing-providers" element={<AdminBillingProvidersPage />} />
            {/* OCP-525 - Terms versions + acceptance gaps. */}
            <Route path="terms" element={<AdminTermsPage />} />
            {/* OCP-597 - persisted operator notification inbox. */}
            <Route path="notifications" element={<AdminNotificationsPage />} />
            {/* OCP-608 - operator P&L: monthly costs against revenue. */}
            <Route path="finance" element={<AdminFinancePage />} />
            {/* OCP-632 - continuous chat-model evaluation: runs, trend, cases. */}
            <Route path="ai/quality" element={<AdminAiQualityPage />} />
            <Route path="portal-codes" element={<AdminPortalCodesPage />} />
            <Route path="waitlist" element={<AdminWaitlistPage />} />
            <Route path="selfhost-notify" element={<AdminSelfHostNotifyPage />} />
            <Route path="provisioning-approvals" element={<AdminProvisioningApprovalsPage />} />
            <Route path="licenses" element={<AdminLicensesPage />} />
            <Route path="licenses/:id" element={<AdminLicenseDetailPage />} />
            <Route path="audit" element={<AdminAuditPage />} />
            <Route path="settings" element={<AdminSettingsPage />} />
            <Route path="team" element={<AdminTeamPage />} />
            {/* OCP-930 - agents' requests for person-level actions; a person approves with a fresh TOTP code. */}
            <Route path="approvals" element={<AdminApprovalsPage />} />
            <Route path="approvals/:id" element={<AdminApprovalDetailPage />} />
            {/* OCP-755 - the operator's own second factor. */}
            <Route path="security" element={<SecurityPage />} />
            <Route path="encryption" element={<AdminEncryptionPage />} />
            {/* OCP-755 - control-plane DB backups + restore drills (the backup alerts link here). */}
            <Route path="ops/backups" element={<AdminBackupsPage />} />
            {/* OCP-423 - tenant file backups, restores, the file drill (the file-backup alerts link here). */}
            <Route path="ops/file-backups" element={<AdminFileBackupsPage />} />
            {/* OCP-755 - the quarterly access review. */}
            <Route path="ops/access-review" element={<AdminAccessReviewPage />} />
            <Route path="lifecycle/hard-delete" element={<AdminHardDeleteQueuePage />} />
            <Route path="payment-defaults" element={<AdminPaymentDefaultsPage />} />
            <Route path="code-grants" element={<AdminCodeGrantsPage />} />
            <Route path="discount-codes" element={<AdminDiscountCodesPage />} />
            <Route path="company-packages" element={<AdminCompanyPackagesPage />} />
            <Route path="observability" element={<AdminObservabilityPage />} />
            {/* OMS operator surface (OCP-133..139). Pages under
                admin.orboto.io/oms/* — one entry point per resource. */}
            <Route path="oms/customers" element={<OmsCustomersPage />} />
            <Route path="oms/customers/:id" element={<OmsCustomerDetailPage />} />
            <Route path="oms/sends" element={<OmsSendsPage />} />
            <Route path="oms/suppression" element={<OmsSuppressionPage />} />
            <Route path="oms/quotas" element={<OmsQuotasPage />} />
            <Route path="oms/templates" element={<OmsTemplatesPage />} />
            <Route path="oms/system-status" element={<OmsSystemStatusPage />} />
            {/* OCP-206 — operator-internal mail (OCP's own sends). */}
            <Route path="mail/operator/sends" element={<AdminMailOperatorSendsPage />} />
            <Route path="mail/operator/suppressions" element={<AdminMailOperatorSuppressionsPage />} />
            {/* OCP-633 - operator announcements (preview, send, history). */}
            <Route path="mail/announcements" element={<AdminMailAnnouncementsPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
